/* eslint-disable import/prefer-default-export */
import axios from 'axios';
import { getLoginUser } from './loginService';
import { isCallcenterLoggedIn, getCallcenterUser } from './callcenterService';

const baseUrl = `${process.env.BACKEND_API_BASE_URL}/jobs.php`;

const getToken = () => (isCallcenterLoggedIn() ? getCallcenterUser() : getLoginUser()).token;

export const jobsService = {
  get_jobs: async data =>
    axios.get(baseUrl, {
      params: {
        method: 'get_jobs',
        token: getToken(),
        ...data, // { dbname }
      },
    }),
  get_jobs_by_ids: async data =>
    axios.get(baseUrl, {
      params: {
        method: 'get_jobs_by_ids',
        token: getToken(),
        ...data, // { ids }
      },
    }),
  get_reservation_jobs: async data =>
    axios.get(baseUrl, {
      params: {
        method: 'get_reservation_jobs',
        token: getToken(),
        ...data, // { order_by, order, search, page_size, page_num, dbname }
      },
    }),
  get_routeview_jobs: async data =>
    axios.get(baseUrl, {
      params: {
        method: 'get_routeview_jobs',
        token: getToken(),
        ...data, // { order_by, order, dbname, reservationDate}
      },
    }),
  get_routemapview_jobs: async data =>
    axios.get(baseUrl, {
      params: {
        method: 'get_routemapview_jobs',
        token: getToken(),
        ...data, // { driver_id, reservation_date}
      },
    }),
  get_declined_jobs: async data =>
    axios.get(baseUrl, {
      params: {
        method: 'get_declined_jobs',
        token: getToken(),
        ...data, // { order_by, order, dbname }
      },
    }),
  get_expired_jobs: async data =>
    axios.get(baseUrl, {
      params: {
        method: 'get_expired_jobs',
        token: getToken(),
        ...data, // { window: 1w|2w|3w|4w|2m|3m, order_by, order, search, page_size, page_num, dbname }
      },
    }),
  get_job: async data =>
    axios.get(baseUrl, {
      params: {
        method: 'get_job',
        token: getToken(),
        ...data, // { job_id, dbname }
      },
    }),
  get_job_payments: async data =>
    axios.get(baseUrl, {
      params: {
        method: 'get_job_payments',
        token: getToken(),
        ...data, // { job_id, dbname }
      },
    }),
  get_tags: async data =>
    axios.get(baseUrl, {
      params: {
        method: 'get_tags',
        token: getToken(),
        ...data, // { job_id, dbname }
      },
    }),
  save_job: async data =>
    axios.get(baseUrl, {
      params: {
        method: 'save_job',
        token: getToken(),
        ...data, // { job_id, is_dispatch, job_info, dbname }
      },
    }),
  save_job_step: async data =>
    axios.get(baseUrl, {
      params: {
        method: 'save_job_step',
        token: getToken(),
        ...data, // { job_id, step, job_info, dbname }
      },
    }),
  get_cancel_reason: async data =>
    axios.get(`${process.env.BACKEND_API_BASE_URL}/kinetik1.php`, {
      params: {
        method: 'getAccountDetail',
        token: getToken(),
        ...data,
      },
    }),
  cancel_job: async data =>
    axios.get(baseUrl, {
      params: {
        method: 'cancel_job',
        token: getToken(),
        ...data,
      },
    }),
  confirm_reservation: async data =>
    axios.get(baseUrl, {
      params: {
        method: 'confirm_reservation',
        token: getLoginUser().token,
        ...data,
      },
    }),
  set_passenger_reservation_status: async (jobId, passengerStatus, data = {}) =>
    axios.get(baseUrl, {
      params: {
        method: 'set_passenger_reservation_status',
        job_id: jobId,
        passenger_status: passengerStatus,
        token: getToken(),
        ...data,
      },
    }),
  assign_driver_to_job: async (jobId, driverId, tagOverride) =>
    axios.get(baseUrl, {
      params: {
        method: 'assign_driver_to_job',
        job_id: jobId,
        driver_id: driverId,
        ...(tagOverride && { tag_override: 1 }),
        token: getLoginUser().token,
      },
    }),
  bulk_assign_driver_to_job: async (assign_info) =>
    axios.get(baseUrl, {
      params: {
        method: 'bulk_assign_driver_to_job',
        assign_info,
        token: getLoginUser().token,
      },
    }),
  change_reservation_time: async (job_id, reservation_time) =>
    axios.get(baseUrl, {
      params: {
        method: 'change_reservation_time',
        job_id,
        reservation_time,
        token: getLoginUser().token,
      },
    }),
  dispatch_driver_to_job: async (jobId, driverId, vehicleId, tagOverride) =>
    axios.get(baseUrl, {
      params: {
        method: 'dispatch_driver_to_job',
        job_id: jobId,
        driver_id: driverId,
        vehicle_id: vehicleId,
        ...(tagOverride && { tag_override: 1 }),
        token: getLoginUser().token,
      },
    }),
  admin_get_jobs_table: async (filter, order, page, pageSize) =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_get_jobs_table',
        ...filter,
        order,
        page,
        page_size: pageSize,
        token: getLoginUser().token,
      },
    }),
  admin_attest_all: async filter =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_attest_all',
        ...filter,
        token: getLoginUser().token,
      },
    }),
  admin_get_mas: async jobId =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_get_mas',
        job_id: jobId,
        token: getLoginUser().token,
      },
    }),
  admin_job_attest: async jobId =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_job_attest',
        job_id: jobId,
        token: getLoginUser().token,
      },
    }),
  admin_job_kinetik_claim: async jobId =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_job_kinetik_claim',
        job_id: jobId,
        token: getLoginUser().token,
      },
    }),
  // Kinetik claims: dry run (what would be sent, what is excluded and why)
  admin_kinetik_claim_preview: async jobIds =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_kinetik_claim_preview',
        job_ids: jobIds.join(','),
        token: getLoginUser().token,
      },
    }),
  // Kinetik claims: submit the selected jobs (plus their same-day legs) as one batch
  admin_kinetik_claim_bulk: async jobIds =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_kinetik_claim_bulk',
        job_ids: jobIds.join(','),
        token: getLoginUser().token,
      },
    }),
  admin_kinetik_claim_detail: async jobId =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_kinetik_claim_detail',
        job_id: jobId,
        token: getLoginUser().token,
      },
    }),
  admin_kinetik_claim_sync: async jobId =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_kinetik_claim_sync',
        job_id: jobId,
        token: getLoginUser().token,
      },
    }),
  admin_job_correction: async data =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_job_correction',
        ...data,
        token: getLoginUser().token,
      },
    }),
  admin_job_mas_resubmit: async data =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_job_mas_resubmit',
        ...data,
        token: getLoginUser().token,
      },
    }),
  admin_resend_receipt: async (jobId, receiptEmail) =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_resend_receipt',
        job_id: jobId,
        receipt_email: receiptEmail,
        token: getLoginUser().token,
      },
    }),
  admin_get_payment_detail: async job_payment_id =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_get_payment_detail',
        job_payment_id,
        token: getLoginUser().token,
      },
    }),
  admin_save_prior_auth: async (jobId, priorAuthNumber) =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_save_prior_auth',
        job_id: jobId,
        prior_auth_number: priorAuthNumber,
        token: getLoginUser().token,
      },
    }),
  admin_set_status: async (jobId, status) =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_set_status',
        job_id: jobId,
        status,
        token: getLoginUser().token,
      },
    }),
  admin_edit_fare: async (jobId, data) =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_edit_fare',
        job_id: jobId,
        ...data,
        token: getLoginUser().token,
      },
    }),
  job_process_payment: async (jobId, amount, token = null) =>
    axios.get(baseUrl, {
      params: {
        method: 'job_process_payment',
        job_id: jobId,
        amount,
        token: token || getLoginUser().token,
      },
    }),
  job_charge_booking_deposit: async (jobId, token = null) =>
    axios.get(baseUrl, {
      params: {
        method: 'job_charge_booking_deposit',
        job_id: jobId,
        token: token || getLoginUser().token,
      },
    }),
  admin_mas_sync_import: async () =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_mas_sync_import',
        token: getLoginUser().token,
      },
    }),
  admin_import_jobs: async (jobData) =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_import_jobs',
        data: jobData,
        token: getLoginUser().token,
      },
    }),
  admin_export_jobs_table: async (filter, order, export_type) =>
    axios.get(baseUrl, {
      params: {
        method: 'admin_export_jobs_table',
        ...filter,
        order,
        export_type,
        token: getLoginUser().token,
      },
      responseType: 'blob',
    }),
};
